import { useState } from "react";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Loader2, Copy, Check, Download } from "lucide-react";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApiTest = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(false);

  const runTest = async () => {
    setLoading(true);
    setResults(null);
    try {
      const res = await fetch(`${API}/jisr/diagnostic-report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setResults(data);
    } catch (err) {
      setResults({ error: err.message });
    }
    setLoading(false);
  };

  const copyReport = () => {
    const text = generateTextReport(results);
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadReport = () => {
    const text = generateTextReport(results);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jisr-api-diagnostic-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateTextReport = (data) => {
    if (!data) return '';
    return `
════════════════════════════════════════════════════════════════
   JISR OPEN API - DIAGNOSTIC REPORT
   Generated: ${data.report_generated || new Date().toISOString()}
   Organization: ${data.api_config?.slug || 'N/A'}
   API URL: ${data.api_config?.api_url || 'N/A'}
   API Key: ${data.api_config?.api_key_prefix || 'N/A'}
════════════════════════════════════════════════════════════════

1. AUTHENTICATION
   Status: ${data.auth_status || 'N/A'}
   ${data.auth_status === 'OK' ? '✓ API credentials are valid and authentication succeeds.' : '✗ Authentication failed.'}

2. ATTENDANCE LOGS (GET /openapi/v1/attendance_logs)
   ─────────────────────────────────────────────────
${(data.attendance_logs_tests || []).map(t => `
   Test: ${t.name}
   Parameters: ${JSON.stringify(t.params_sent)}
   HTTP Status: ${t.status_code}
   API Success: ${t.success}
   Punches Returned: ${t.punches_count}
   Total Entries: ${t.total_entries}
   ${t.punches_count > 0 ? `Sample: ${JSON.stringify(t.sample_punches, null, 4)}` : '→ No punch records found.'}`).join('\n')}

3. ATTENDANCE SUMMARY (GET /openapi/v1/attendance/summary)
   ──────────────────────────────────────────────────────
   Total Employee Records: ${data.summary_comparison?.total_records || 0}
   Employees with worked_days > 0: ${data.summary_comparison?.employees_worked || 0}
   ${data.summary_comparison?.employees_worked > 0 ? '→ Attendance IS being tracked (employees show worked days).' : ''}
   Sample records:
${(data.summary_comparison?.sample || []).map(s => `     Code ${s.code}: worked_days=${s.worked_days}, hours=${s.total_working_hours}, leave=${s.leave_days}`).join('\n')}

4. CONCLUSION
   ───────────
   ${data.conclusion || ''}

════════════════════════════════════════════════════════════════
   This report was auto-generated to assist Jisr support in
   diagnosing why biometric device punches are not returned
   by the Open API GET /attendance_logs endpoint.
════════════════════════════════════════════════════════════════
`.trim();
  };

  const StatusBadge = ({ ok }) => (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono rounded ${ok ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/15 text-red-400 border border-red-500/20'}`}>
      {ok ? '✓ PASS' : '✗ FAIL'}
    </span>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin')}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-white">Jisr API Diagnostic</h1>
            <p className="text-xs text-zinc-500">Generate a report for Jisr support</p>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
          <p className="text-sm text-zinc-400">
            This runs a full diagnostic on the Jisr <code className="text-emerald-400 bg-zinc-800 px-1 rounded">attendance_logs</code> endpoint 
            and generates a report you can share with Jisr support.
          </p>

          <Button onClick={runTest} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="run-test-btn">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Running diagnostic...</> : <><Play className="w-4 h-4 mr-2" /> Run Diagnostic</>}
          </Button>

          {results && !results.error && (
            <div className="space-y-4">
              {/* Action buttons */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={copyReport}
                  className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                  {copied ? <><Check className="w-3 h-3 mr-1 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy Report</>}
                </Button>
                <Button variant="outline" size="sm" onClick={downloadReport}
                  className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                  <Download className="w-3 h-3 mr-1" /> Download .txt
                </Button>
              </div>

              {/* Auth Status */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-wider">1. Authentication</span>
                  <StatusBadge ok={results.auth_status === 'OK'} />
                </div>
                <p className="text-sm text-zinc-300">API credentials valid, token received.</p>
              </div>

              {/* Attendance Logs Tests */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-wider">2. Attendance Logs (GET)</span>
                  <StatusBadge ok={results.attendance_logs_tests?.some(t => t.punches_count > 0)} />
                </div>
                <div className="space-y-3">
                  {results.attendance_logs_tests?.map((test, i) => (
                    <div key={i} className="border border-zinc-800 rounded-md p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-zinc-300">{test.name}</span>
                        <span className={`text-xs font-mono ${test.punches_count > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {test.punches_count} punches
                        </span>
                      </div>
                      <p className="text-[10px] font-mono text-zinc-600">
                        HTTP {test.status_code} · Params: {JSON.stringify(test.params_sent)}
                      </p>
                      {test.sample_punches?.length > 0 && (
                        <pre className="mt-2 text-[10px] font-mono text-emerald-400/70 bg-black/50 rounded p-2 overflow-auto">
                          {JSON.stringify(test.sample_punches, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary Comparison */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-mono text-zinc-500 uppercase tracking-wider">3. Attendance Summary (comparison)</span>
                  <StatusBadge ok={results.summary_comparison?.employees_worked > 0} />
                </div>
                <p className="text-sm text-zinc-300 mb-2">
                  <span className="text-white font-medium">{results.summary_comparison?.employees_worked || 0}</span> employees show <code className="text-emerald-400 bg-zinc-800 px-1 rounded text-xs">worked_days &gt; 0</code> today
                  (out of {results.summary_comparison?.total_records || 0} total)
                </p>
                <p className="text-xs text-zinc-500">
                  This confirms attendance IS being recorded in Jisr, but not appearing in attendance_logs.
                </p>
              </div>

              {/* Conclusion */}
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                <span className="text-xs font-mono text-amber-400 uppercase tracking-wider">4. Conclusion</span>
                <p className="text-sm text-amber-200/80 mt-2 whitespace-pre-line">{results.conclusion}</p>
              </div>
            </div>
          )}

          {results?.error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p className="text-sm text-red-400 font-mono">{results.error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiTest;
